import clsx from 'clsx';
import { Property } from 'csstype';
import {
  type ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { _mergeProps } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import {
  textAreaContainerCSS,
  textAreaCSS,
  textAreaResizerCSS,
  textAreaWidths,
} from './TextArea.sty.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import type { FocusEvents, KeyboardEvents } from '../../core/types/events.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { withFormFieldMessageContextProvider } from '../forms-core/components/form-field/FormFieldMessageContextProvider.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useInputHint } from '../hooks/useInputHint.js';
import type { FormControlRef } from '../shared-types.js';

// #region Typings
/**
 * @public
 */
export type TextAreaProps = FormControlProps<
  string,
  (value: string, event: ChangeEvent<HTMLTextAreaElement>) => void | never
> &
  FocusEvents &
  StylingProps &
  DataTestId &
  MaskingProps &
  KeyboardEvents & {
    placeholder?: string;
    /**
     * This attribute indicates whether the textarea is resizable by the user, and, if so, in which direction.
     * @defaultValue 'both'
     */
    resize?: 'both' | 'vertical' | 'horizontal' | 'none';
    /**
     * The visible width of the text control, in average character widths.
     * If it is specified, it must be a positive integer.
     * @defaultValue 20
     */
    cols?: number;
    /**
     * The number of visible text lines for the control. If it is specified, it must be a positive integer.
     * @defaultValue 2
     */
    rows?: number;
    /**
     * The minimum number of characters required that the user should enter.
     * Doesn't stop the user from removing characters so that the number entered goes past the minimum,
     * but it does make the value entered into the <textarea> invalid.
     * With a set minlength an empty <textarea> is still considered valid unless you also have the required attribute set.
     */
    minLength?: number;
    /**
     * The maximum number of characters that the user can enter. If this value isn't specified,
     * the user can enter an unlimited number of characters.
     */
    maxLength?: number;
    /**
     * This attribute indicates whether the value of the control can be automatically completed by the browser.
     * If no autocompletion is desired, it can be set to "off".
     */
    autoComplete?: string;
    /**
     * The name of the control - used when submitting the textarea in a form.
     * @defaultValue 'full'
     */
    width?: 'content' | 'full' | Property.Width<string & Record<string, never>>;
  };

/**
 * @public
 */
export type TextAreaInputRef = Omit<
  FormControlRef<HTMLDivElement>,
  'inputRef'
> & {
  readonly inputRef?: HTMLTextAreaElement | null;
};
// #endregion

const TextAreaRoot = /* @__PURE__ */ forwardRef<
  TextAreaInputRef,
  TextAreaProps
>((props, forwardedRef) => {
  const {
    onChange,
    minLength,
    maxLength,
    id: propId,
    disabled: propDisabled,
    required: propRequired,
    readOnly = false,
    resize = 'both',
    controlState,
    value: propValue,
    defaultValue,
    width = 'full',
    'aria-describedby': ariaDescribedby,
    'aria-disabled': ariaDisabledProp,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  }: TextAreaProps = props;

  const { disabled, required, id, ariaDisabled } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled: ariaDisabledProp,
  });
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const { state: messagesContextState, dispatch: messagesContextDispatch } =
    useFormFieldMessagesContext();

  // Imperative Handle Construction
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(forwardedRef, (): TextAreaInputRef => {
    return {
      element: containerRef.current,
      inputRef: textareaRef.current,
      focus() {
        textareaRef.current?.focus();
      },
      validate() {
        textareaRef.current?.checkValidity();
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      },
    };
  });

  const [controlStateHasError, hint] = useFormControlState(controlState);
  const hasError = controlState
    ? controlStateHasError
    : messagesContextState.showError;

  const [value, setValue] = useControllableState({
    defaultValue,
    value: propValue,
  });

  const {
    onChange: validationOnChange,
    onBlur: validationOnBlur,
    onInvalid: validationOnInvalid,
    ariaProps,
  } = useFormFieldValidation(textareaRef);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target?.value;
    setValue(newValue);
    onChange?.(newValue, event);
    !controlState && validationOnChange();
  };

  // _useInputHint will be replaced with new validation concept. APPDEV-8298
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { inputProps, hintProps } = useInputHint({
    ariaDescribedby,
    hint,
    hasError,
  });

  const {
    focusProps: { onFocus, onBlur },
    focusClassName,
  } = useFocusRing({
    color: hasError ? 'critical' : 'neutral',
    ignoreModality: true,
    disabled,
  });

  return (
    <Flex
      gap={4}
      flexDirection="column"
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={containerRef}
      className={consumerClassName}
      style={consumerStyle}
    >
      <Flex gap={0}>
        <textarea
          ref={textareaRef}
          id={id}
          minLength={minLength}
          maxLength={maxLength}
          className={clsx(
            {
              'strato-form-error': hasError,
              'strato-form-resizable': resize !== 'none',
              [`${textAreaWidths.content}`]: width === 'content',
              [`${textAreaWidths.full}`]: width === 'full',
            },
            textAreaCSS({
              resize,
              disabled: disabledOrAriaDisabled,
            }),
            focusClassName,
          )}
          style={{
            width: width !== 'content' && width !== 'full' ? width : undefined,
          }}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          value={value}
          aria-required={required}
          aria-disabled={ariaDisabled}
          onChange={handleChange}
          onInvalid={(e) => !controlState && validationOnInvalid(e)}
          onFocus={onFocus}
          onBlur={(e) => {
            onBlur(e);
            !controlState && validationOnBlur(e);
          }}
          {..._mergeProps(
            remainingProps,
            controlState ? inputProps : ariaProps,
          )}
        />
        {resize !== 'none' && (
          <div className={textAreaContainerCSS}>
            <span
              className={textAreaResizerCSS({
                hasError,
                disabled: disabledOrAriaDisabled,
              })}
            />
          </div>
        )}
      </Flex>
      {hint && (
        <BaseItem
          variant={hasError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </BaseItem>
      )}
    </Flex>
  );
});

/**
 * The `TextArea` component can be used as a multi-line plain-text editing control to
 * enter free-form text, such as a comment on a form.
 * The component uses a fallback FormFieldMessages context if it wasn't wrapped in a FormField.
 * @public
 */
export const TextArea = withFormFieldMessageContextProvider<
  TextAreaProps,
  TextAreaInputRef
>(TextAreaRoot);

(TextArea as typeof TextArea & { displayName: string }).displayName =
  'TextArea';
